import { Link } from 'react-router-dom';
import type { Service } from '@/mocks/services';
import { getCategoryById, formatViews } from '@/lib/content';

interface ServiceCardProps {
  service: Service;
  variant?: 'default' | 'compact';
}

export default function ServiceCard({ service, variant = 'default' }: ServiceCardProps) {
  const category = getCategoryById(service.categoryId);

  if (variant === 'compact') {
    return (
      <Link
        to={`/detail/${service.id}`}
        className="group flex gap-3 p-3 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
      >
        <div className="w-24 h-20 shrink-0 rounded-md overflow-hidden bg-background-100">
          <img src={service.image} alt={service.title} title={`${service.title} - 在线服务平台`} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300" />
        </div>
        <div className="min-w-0 flex flex-col justify-between">
          <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
            {service.title}
          </h3>
          <div className="flex items-center gap-3 text-xs text-foreground-500 mt-1">
            <span className="flex items-center gap-1"><i className="ri-eye-line"></i>{formatViews(service.views)}</span>
            <span className="flex items-center gap-1 text-accent-600"><i className="ri-star-fill"></i>{service.rating}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/detail/${service.id}`}
      className="group flex flex-col rounded-lg overflow-hidden bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer h-full"
    >
      <div className="relative w-full h-44 overflow-hidden bg-background-100">
        <img
          src={service.image}
          alt={service.title}
          title={`${service.title} - 在线服务平台`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <span className="absolute top-3 left-3 px-2.5 py-1 text-xs rounded-full bg-background-50/90 text-primary-700 font-medium">
          {category?.name}
        </span>
        {service.featured && (
          <span className="absolute top-3 right-3 px-2.5 py-1 text-xs rounded-full bg-accent-500 text-background-50 font-medium">
            精选推荐
          </span>
        )}
      </div>
      <div className="flex flex-col flex-1 p-4 md:p-5">
        <h3 className="text-base font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
          {service.title}
        </h3>
        <p className="mt-2 text-sm text-foreground-600 line-clamp-2 flex-1">{service.summary}</p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {service.tags.slice(0, 2).map((tag) => (
            <span key={tag} className="px-2 py-0.5 text-xs rounded-md bg-secondary-100 text-secondary-800">
              {tag}
            </span>
          ))}
        </div>
        <div className="mt-4 pt-3 border-t border-background-100 flex items-center justify-between text-xs text-foreground-500">
          <span className="flex items-center gap-1 truncate">
            <i className="ri-store-2-line"></i>
            <span className="truncate max-w-[8rem]">{service.provider}</span>
          </span>
          <span className="flex items-center gap-3 shrink-0">
            <span className="flex items-center gap-1"><i className="ri-eye-line"></i>{formatViews(service.views)}</span>
            <span className="flex items-center gap-1 text-accent-600"><i className="ri-star-fill"></i>{service.rating}</span>
          </span>
        </div>
      </div>
    </Link>
  );
}